<template>
    <popup-modal ref="popup"
                 :close-on-click="true"
                 :customClasses="customClass ? customClass : (fullSize ? 'flex flex-col' : '')"
                 :full-size="fullSize"
                 :hasCloseButton="true"
                 :dirty="form.isDirty()"
                 :position="position"
                 :use-portal="usePortal"
                 @close="$emit('close')"
    >
        <div class="-mt-24 border-b-2 border-primary-20 mb-24">
            <div class="flex items-center mb-24">
                <div class="mr-auto">
                    <template v-if="titleAsHtml">
                        <h1 class="mr-48 text-heading-2 font-semibold first-letter:uppercase" v-html="title"></h1>
                        <p v-if="subtitle" class="mr-24 text-primary-80" v-html="subtitle"></p>
                    </template>
                    <template v-else>
                        <h1 class="mr-48 text-heading-2 font-semibold first-letter:uppercase">
                            {{ title }}
                        </h1>
                        <p v-if="subtitle" class="mr-24 text-primary-80">
                            {{ subtitle }}
                        </p>
                    </template>
                </div>

                <template v-if="!hideTopButtons">
                    <simple-button class="mr-8" @click="closePopup">
                        {{ $t('general.cancel') }}
                    </simple-button>

                    <simple-button
                        :disabled="buttonDisabled"
                        display="secondary"
                        icon="save"
                        :loading="form.submitting"
                        look="primary"
                        @click="submitForm"
                    >
                        {{ $t('general.save') }}
                    </simple-button>
                </template>
            </div>
        </div>

        <tabs
            v-if="translatable"
            :activeKey="selectedLocale"
            :items="languagesTabs"
            :errors="warningTabs"
            class="mb-16"
            @set-active="selectLanguage($event)"
            scroll
        ></tabs>

        <div :class="[ singleButton ? '-mb-16' : '', fullSize ? 'flex-grow' : '' ]">
            <slot></slot>
        </div>

        <div v-if="!singleButton" class="pt-32">
            <div class="flex items-center justify-between pt-24 -mb-24 border-t border-primary-20">
                <div class="flex items-center">
                    <slot name="bottomleft"/>
                </div>
                <div class="flex items-center">
                    <template v-if="closeInsteadOfSave">
                        <simple-button class="mr-8" display="secondary" @click="closePopup">
                            {{ $t('general.close') }}
                        </simple-button>
                    </template>
                    <template v-else>
                        <simple-button class="mr-8" @click="closePopup">
                            {{ $t('general.cancel') }}
                        </simple-button>

                        <simple-button
                            :disabled="buttonDisabled"
                            display="secondary"
                            icon="save"
                            :loading="form.submitting"
                            look="primary"
                            @click="submitForm"
                        >
                            {{ $t('general.save') }}
                        </simple-button>
                    </template>
                </div>
            </div>
        </div>

    </popup-modal>
</template>

<script>
import SvgIcon from './SvgIcon.vue';
import PopupModal from './PopupModal.vue';
import SimpleButton from './SimpleButton.vue';
import Form from '../../etc/forms/Form';
import Tabs from '../navigation/Tabs';
import {mapStores} from "pinia";
import {useMainStore} from "../../state/store";

export default {
    components: {
        'svg-icon': SvgIcon,
        'popup-modal': PopupModal,
        'simple-button': SimpleButton,
        Tabs,
    },
    props: {
        title: String,
        subtitle: {type: String, required: false},
        position: {type: String, default: 'center'},
        form: Form,
        label: {type: String, default: 'title'},
        singleButton: Boolean,
        translatable: {type: Boolean, default: false},
        fullSize: {
            required: false,
            type: Boolean,
            default: false,
        },
        titleAsHtml: {
            required: false,
            type: Boolean,
            default: false,
        },
        buttonsAlwaysActive: {
            required: false,
            type: Boolean,
            default: false,
        },
        alwaysSubmit: {
            required: false,
            type: Boolean,
            default: false,
        },
        customClass: {
            type: String,
            required: false
        },
        hideTopButtons: Boolean,
        closeInsteadOfSave: Boolean,
        usePortal: {type: Boolean, default: true},
        isEmailTemplate: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapStores(useMainStore),
        currentCompany() {
            return this.mainStore.currentCompany;
        },
        selectedLocale() {
            return this.mainStore.selectedLocale;
        },
        buttonDisabled() {
            if (this.buttonsAlwaysActive) {
                return false;
            }
            return !this.form.isValid();
        },
        languages() {
            return this.currentCompany.jobsite.active_languages;
        },
        languagesTabs() {
            if (this.currentCompany.jobsite.active_languages.length === 1) {
                return;
            }

            let languages = {};

            this.currentCompany.jobsite.active_languages.forEach((field, index) => {
                languages[field] = {
                    title: this.$t('languages.' + field + '.title'),
                };
            });

            return languages;
        },
        warningTabs() {
            let languages = this.languages;
            let errorLang = [];

            for (let index in languages) {
                if (!this.form.data[this.label][languages[index]]) {
                    errorLang.push(languages[index]);
                }
            }
            return errorLang;
        },
    },
    methods: {
        closePopup() {
            this.$refs.popup.close();
        },
        submitForm() {
            if (!this.form.isDirty() && !this.alwaysSubmit) {
                return this.closePopup();
            }
            return this.$emit('submit');
        },
        selectLanguage(language) {
            this.mainStore.setSelectedLocale(language);
        },
    },
    mounted() {
        if (!!this.translatable && !this.languagesTabs) {
            this.selectLanguage(this.languages[0]);
        }
    }
}
</script>
